import React, {
  useState,
  useEffect,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import {
  FiEdit,
  FiTrash2,
  FiX,
  FiSearch,
} from "react-icons/fi";

import {
  FaUserTie,
  FaBuilding,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";

import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";

import {
  postData,
} from "../../../services/FetchNodeservices";

export default function InterviewReport() {

  // =================================================
  // LOCATION
  // =================================================

  const location =
    useLocation();

  // =================================================
  // STATES
  // =================================================

  const [filters, setFilters] =
    useState({

      search: "",

      interviewDate: "",

      company: "",

      position: "",

      stage: "",
    });

  const [data, setData] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [openForm, setOpenForm] =
    useState(false);

  const [editingRowId, setEditingRowId] =
    useState(null);

  const [page, setPage] =
    useState(1);

  const rowsPerPage = 10;

  const [formValues, setFormValues] =
    useState({

      company: "",

      position: "",

      location: "",

      candidate_name: "",

      contact_no: "",

      email_id: "",

      mode_of_interview: "",

      interview_date:
        dayjs().format(
          "YYYY-MM-DD"
        ),

      interview_time: "",

      stage: "",
    });

  // =================================================
  // LOAD
  // =================================================

  useEffect(() => {

    loadData();

  }, []);

  // =================================================
  // AUTO TODAY FILTER
  // =================================================

  useEffect(() => {

    const params =
      new URLSearchParams(
        location.search
      );

    const filter =
      params.get("filter");

    if (
      filter === "today"
    ) {

      setFilters((prev) => ({

        ...prev,

        interviewDate:
          dayjs().format(
            "YYYY-MM-DD"
          ),
      }));
    }

  }, [location.search]);

  // =================================================
  // LOAD DATA
  // =================================================

  const loadData =
    async () => {

      try {

        setLoading(true);

        const result =
          await postData(
            "interview/fetch",
            {}
          );

        if (
          result?.status
        ) {

          setData(
            result.data || []
          );
        }

      } catch (error) {

        Swal.fire(
          "Error",
          "Failed to load interviews",
          "error"
        );

      } finally {

        setLoading(false);
      }
    };

  // =================================================
  // UNIQUE DROPDOWNS
  // =================================================

  const companyOptions =
    [
      ...new Set(
        data.map(
          (item) =>
            item.company
        )
      ),
    ];

  const positionOptions =
    [
      ...new Set(
        data.map(
          (item) =>
            item.position
        )
      ),
    ];

  const stageOptions =
    [
      ...new Set(
        data.map(
          (item) =>
            item.stage
        )
      ),
    ];

  // =================================================
  // FILTER DATA
  // =================================================

  const filteredData =
    data.filter(
      (row) => {

        const search =
          filters.search.toLowerCase();

        const matchesSearch =

          row.company
            ?.toLowerCase()
            .includes(search) ||

          row.position
            ?.toLowerCase()
            .includes(search) ||

          row.candidate_name
            ?.toLowerCase()
            .includes(search) ||

          row.location
            ?.toLowerCase()
            .includes(search);

        const matchesDate =

          !filters.interviewDate ||

          dayjs(
            row.interview_date
          ).format(
            "YYYY-MM-DD"
          ) ===
            filters.interviewDate;

        const matchesCompany =

          !filters.company ||

          row.company ===
            filters.company;

        const matchesPosition =

          !filters.position ||

          row.position ===
            filters.position;

        const matchesStage =

          !filters.stage ||

          row.stage ===
            filters.stage;

        return (

          matchesSearch &&
          matchesDate &&
          matchesCompany &&
          matchesPosition &&
          matchesStage
        );
      }
    );

  // =================================================
  // PAGINATION
  // =================================================

  const paginated =
    filteredData.slice(

      (page - 1) *
        rowsPerPage,

      page *
        rowsPerPage
    );

  // =================================================
  // RESET FILTERS
  // =================================================

  const resetFilters =
    () => {

      setFilters({

        search: "",

        interviewDate: "",

        company: "",

        position: "",

        stage: "",
      });

      setPage(1);
    };

  // =================================================
  // EXPORT EXCEL
  // =================================================

  const exportExcel =
    () => {

      const ws =
        XLSX.utils.json_to_sheet(
          filteredData
        );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Interview Report"
      );

      XLSX.writeFile(
        wb,
        "Interview_Report.xlsx"
      );
    };

  // =================================================
  // EXPORT PDF
  // =================================================

  const exportPDF =
    () => {

      const doc =
        new jsPDF();

      doc.text(
        "Interview Report",
        14,
        15
      );

      doc.autoTable({

        head: [[

          "Company",

          "Candidate",

          "Position",

          "Date",

          "Status",
        ]],

        body:
          filteredData.map(
            (r) => ([

              r.company,

              r.candidate_name,

              r.position,

              dayjs(
                r.interview_date
              ).format(
                "YYYY-MM-DD"
              ),

              r.stage,
            ])
          ),
      });

      doc.save(
        "Interview_Report.pdf"
      );
    };

  // =================================================
  // OPEN MODAL
  // =================================================

  const openModal =
    (row) => {

      setEditingRowId(
        row.id
      );

      setFormValues({

        company:
          row.company,

        position:
          row.position,

        location:
          row.location,

        candidate_name:
          row.candidate_name,

        contact_no:
          row.contact_no,

        email_id:
          row.email_id,

        mode_of_interview:
          row.mode_of_interview,

        interview_date:
          dayjs(
            row.interview_date
          ).format(
            "YYYY-MM-DD"
          ),

        interview_time:
          row.interview_time,

        stage:
          row.stage,
      });

      setOpenForm(true);
    };

  // =================================================
  // UPDATE
  // =================================================

  const saveRecord =
    async () => {

      const result =
        await postData(

          `interview/update/${editingRowId}`,

          formValues
        );

      if (
        result.status
      ) {

        Swal.fire(
          "Success",
          result.message,
          "success"
        );

        loadData();

        setOpenForm(false);

      } else {

        Swal.fire(
          "Error",
          result.message,
          "error"
        );
      }
    };

  // =================================================
  // DELETE
  // =================================================

  const deleteRecord =
    async (id) => {

      Swal.fire({

        title:
          "Delete Interview?",

        icon:
          "warning",

        showCancelButton:
          true,
      }).then(
        async (res) => {

          if (
            res.isConfirmed
          ) {

            await postData(

              `interview/delete/${id}`,

              {}
            );

            loadData();
          }
        }
      );
    };

  return (

    <div className="md:ml-72 pt-24 px-4 md:px-6 pb-8 min-h-screen text-slate-800">

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div className="flex items-center justify-between mb-6">

        <div>

          <h1 className="text-2xl font-semibold">
            Interview Report
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Candidate interview pipeline
          </p>
        </div>

        <div className="flex gap-3">

          <button
            onClick={exportExcel}
            className="
              bg-black
              text-white
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            Excel
          </button>

          <button
            onClick={exportPDF}
            className="
              border
              border-gray-300
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            PDF
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* FILTERS */}
      {/* ================================================= */}

      <div className="glass-card p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">

          {/* SEARCH */}

          <div className="glass-input flex items-center gap-3 rounded-2xl px-4 py-2.5">

            <FiSearch className="text-gray-400" />

            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) =>
                setFilters({

                  ...filters,

                  search:
                    e.target.value,
                })
              }
              className="w-full bg-transparent outline-none text-sm text-slate-800 dark:text-slate-200"
            />
          </div>

          {/* DATE */}

          <input
            type="date"
            value={
              filters.interviewDate
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                interviewDate:
                  e.target.value,
              })
            }
            className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
          />

          {/* COMPANY */}

          <select
            value={
              filters.company
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                company:
                  e.target.value,
              })
            }
            className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
          >

            <option value="">
              All Company
            </option>

            {companyOptions.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>

          {/* POSITION */}

          <select
            value={
              filters.position
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                position:
                  e.target.value,
              })
            }
            className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
          >

            <option value="">
              All Position
            </option>

            {positionOptions.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>

          {/* STATUS */}

          <select
            value={
              filters.stage
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                stage:
                  e.target.value,
              })
            }
            className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
          >

            <option value="">
              All Status
            </option>

            {stageOptions.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        {/* RESET */}

        <div className="flex justify-end mt-4">

          <button
            onClick={
              resetFilters
            }
            className="
              border
              border-gray-300
              px-5
              py-2
              rounded-2xl
              text-sm
              hover:bg-gray-100
            "
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}

      <div className="glass-card overflow-hidden">

        <div className="overflow-x-auto">

          <table className="w-full">

            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">

              <tr>

                {[
                  "Candidate",
                  "Company",
                  "Position",
                  "Interview",
                  "Status",
                  "Action",
                ].map(
                  (head) => (

                    <th
                      key={head}
                      className="
                        px-5
                        py-4
                        text-left
                        text-xs
                        font-semibold
                        text-gray-500
                      "
                    >
                      {head}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>

              {loading ? (

                <tr>

                  <td
                    colSpan={6}
                    className="
                      text-center
                      py-10
                      text-gray-500
                    "
                  >
                    Loading...
                  </td>
                </tr>

              ) : paginated.length === 0 ? (

                <tr>

                  <td
                    colSpan={6}
                    className="
                      text-center
                      py-10
                      text-gray-500
                    "
                  >
                    No Interviews Found
                  </td>
                </tr>

              ) : (

                paginated.map(
                  (
                    row,
                    index
                  ) => (

                    <tr
                      key={index}
                      className="
                        border-b
                        border-gray-100
                        hover:bg-[#fafafa]
                        transition-all
                      "
                    >

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <div
                            className="
                              w-10
                              h-10
                              rounded-2xl
                              bg-black
                              text-white
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FaUserTie />
                          </div>

                          <div>

                            <h3 className="text-sm font-medium">
                              {
                                row.candidate_name
                              }
                            </h3>

                            <p className="text-xs text-gray-500">
                              {
                                row.location
                              }
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm">

                        <div className="flex items-center gap-2">

                          <FaBuilding className="text-gray-400" />

                          {row.company}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm">
                        {row.position}
                      </td>

                      <td className="px-5 py-4">

                        <div className="space-y-1">

                          <div className="flex items-center gap-2 text-sm">

                            <FaCalendarAlt className="text-gray-400" />

                            {
                              row.interview_date
                                ? dayjs(
                                    row.interview_date
                                  ).format(
                                    "YYYY-MM-DD"
                                  )
                                : "-"
                            }
                          </div>

                          <div className="flex items-center gap-2 text-xs text-gray-500">

                            <FaClock className="text-gray-400" />

                            {
                              row.interview_time
                            }
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">

                        <span
                          className="
                            px-3
                            py-1
                            rounded-full
                            text-xs
                            font-medium
                            bg-black
                            text-white
                          "
                        >
                          {row.stage}
                        </span>
                      </td>

                      <td className="px-5 py-4">

                        <div className="flex gap-2">

                          <button
                            onClick={() =>
                              openModal(
                                row
                              )
                            }
                            className="
                              w-9
                              h-9
                              rounded-xl
                              bg-black
                              text-white
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FiEdit size={14} />
                          </button>

                          <button
                            onClick={() =>
                              deleteRecord(
                                row.id
                              )
                            }
                            className="
                              w-9
                              h-9
                              rounded-xl
                              border
                              border-red-200
                              text-red-500
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}

      <div className="flex justify-end gap-3 mt-5">

        {page > 1 && (

          <button
            onClick={() => setPage(page - 1)}
            className="glass-btn px-4 py-2 rounded-2xl text-sm"
          >
            Prev
          </button>
        )}

        {page *
          rowsPerPage <
          filteredData.length && (

          <button
            onClick={() => setPage(page + 1)}
            className="glass-btn px-4 py-2 rounded-2xl text-sm"
          >
            Next
          </button>
        )}
      </div>

      {/* ================================================= */}
      {/* MODAL */}
      {/* ================================================= */}

      {openForm && (

        <div
          className="
            fixed
            inset-0
            bg-black/50
            z-50
            flex
            items-center
            justify-center
            p-4
          "
        >

          <div
            className="
              bg-white
              w-full
              max-w-2xl
              rounded-3xl
              p-6
              relative
            "
          >

            <button
              onClick={() =>
                setOpenForm(false)
              }
              className="
                absolute
                top-5
                right-5
                text-gray-500
              "
            >
              <FiX size={22} />
            </button>

            <h2 className="text-xl font-semibold mb-6">
              Update Interview
            </h2>

            <div className="grid grid-cols-2 gap-4">

              {[
                {
                  label:
                    "Company",
                  key:
                    "company",
                },

                {
                  label:
                    "Position",
                  key:
                    "position",
                },

                {
                  label:
                    "Location",
                  key:
                    "location",
                },

                {
                  label:
                    "Candidate Name",
                  key:
                    "candidate_name",
                },

                {
                  label:
                    "Contact No",
                  key:
                    "contact_no",
                },

                {
                  label:
                    "Email",
                  key:
                    "email_id",
                },
              ].map(
                (
                  field,
                  index
                ) => (

                  <div
                    key={index}
                  >

                    <label className="text-xs text-gray-500">
                      {
                        field.label
                      }
                    </label>

                    <input
                      className="
                        w-full
                        mt-1
                        border
                        border-gray-200
                        rounded-2xl
                        px-4
                        py-3
                        text-sm
                      "
                      value={
                        formValues[
                          field.key
                        ]
                      }
                      onChange={(
                        e
                      ) =>
                        setFormValues({

                          ...formValues,

                          [field.key]:
                            e.target.value,
                        })
                      }
                    />
                  </div>
                )
              )}
            </div>

            <button
              onClick={saveRecord}
              className="
                mt-6
                w-full
                bg-black
                text-white
                py-3
                rounded-2xl
                font-medium
              "
            >
              Update Interview
            </button>
          </div>
        </div>
      )}
    </div>
  );
}